import { FC, useId } from 'react';

import { Select } from '@new/common/ui/Select/Select';
import type { SelectOption } from '@new/common/ui/Select/Select';
import { ErrorMessage } from '@new/common/ui/ErrorMessage/ErrorMessage';

const LABEL = "Program you're interested in";
const PLACEHOLDER = 'Select a program…';
const NO_PROGRAMS_PLACEHOLDER = 'No programs available';
const NO_PROGRAMS_HINT =
  'There are no programs for this degree yet. Choose another target degree, or contact support.';

interface TargetProgramSelectProps {
  value: number | null;
  options: SelectOption[];
  errors: unknown[];
  onChange: (value: number | null) => void;
  onBlur: () => void;
}

// The form stores the program as a degree id (number); the shared Select works with strings,
// so this field converts at the boundary and renders the same error message as other fields.
export const TargetProgramSelect: FC<TargetProgramSelectProps> = ({
  value,
  options,
  errors,
  onChange,
  onBlur,
}) => {
  const labelId = useId();
  const hasOptions = options.length > 0;
  const messages = errors.map(String);

  const onSelect = (selected: string) => {
    onChange(selected ? Number(selected) : null);
    onBlur();
  };

  return (
    <div className="flex flex-col gap-1.5" role="group" aria-labelledby={labelId}>
      <span id={labelId} className="l4 medium text-(--neutral-black-NB900)!">
        {LABEL}
      </span>
      <Select
        value={value != null ? String(value) : ''}
        onChange={onSelect}
        options={options}
        placeholder={hasOptions ? PLACEHOLDER : NO_PROGRAMS_PLACEHOLDER}
        disabled={!hasOptions}
        hasError={messages.length > 0}
      />
      {hasOptions ? null : (
        <p className="m-0! [font-size:var(--fs-l5)]! text-(--neutral-black-NB600)!">
          {NO_PROGRAMS_HINT}
        </p>
      )}
      <ErrorMessage errors={messages} />
    </div>
  );
};
